import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Button, Spinner, Modal, Badge } from 'react-bootstrap';
import axios from 'axios';
import API_BASE_URL from '../config';

const localizer = momentLocalizer(moment);

export default function GoogleCalendarView({ companyId, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());

  // Event Details Modal
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    if (companyId) {
      fetchCalendarEvents();
    }
  }, [companyId]);

  const fetchCalendarEvents = async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : {};
      const userId = user.id || user.sub;

      const params = new URLSearchParams({
        company_id: companyId,
        ...(userId && { user_id: userId }),
      });

      const res = await axios.get(
        `${API_BASE_URL}/integrations/google/calendar?${params.toString()}`
      );

      const formattedEvents = (res.data || []).map((event) => {
        const start = event.start?.dateTime 
          ? new Date(event.start.dateTime) 
          : new Date(event.start?.date);

        const end = event.end?.dateTime 
          ? new Date(event.end.dateTime) 
          : new Date(event.end?.date);

        return {
          id: event.id,
          title: event.summary || "Untitled Event",
          start,
          end,
          allDay: !event.start?.dateTime,
          resource: event, // Keep original Google event data
        };
      });

      setEvents(formattedEvents);
    } catch (err) {
      console.error("Failed to fetch calendar events:", err);
    } finally {
      setLoading(false);
    }
  };

  // Open Event Details Modal
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const closeEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const handleJoinMeet = () => {
    if (selectedEvent?.resource?.hangoutLink) {
      window.open(selectedEvent.resource.hangoutLink, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading your Google Calendar...</p>
      </div>
    );
  }

  return (
    <div className="p-3">
      {/* Header with View Buttons */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Google Calendar</h4>
        <div>
          <Button 
            variant={view === 'month' ? 'primary' : 'outline-primary'} 
            size="sm" 
            className="me-2"
            onClick={() => setView('month')}
          >
            Month
          </Button>
          <Button 
            variant={view === 'week' ? 'primary' : 'outline-primary'} 
            size="sm" 
            className="me-2"
            onClick={() => setView('week')}
          >
            Week
          </Button>
          <Button 
            variant={view === 'day' ? 'primary' : 'outline-primary'} 
            size="sm"
            onClick={() => setView('day')}
          >
            Day
          </Button>
          {onClose && (
            <Button variant="secondary" size="sm" className="ms-3" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </div>

      {/* Calendar */}
      <div style={{ height: '75vh' }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          style={{ height: '100%' }}
          onSelectEvent={handleSelectEvent}
          popup
          views={['month', 'week', 'day']}
          step={30}
          timeslots={2}
          eventPropGetter={(event) => ({
            style: {
              backgroundColor: event.resource?.hangoutLink ? '#0d6efd' : '#6c757d',
              color: 'white',
              borderRadius: '4px',
              border: 'none',
            },
          })}
        />
      </div>

      {/* Event Details Modal */}
      <Modal show={showEventModal} onHide={closeEventModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Event Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEvent && (
            <div>
              <h5 className="mb-3">{selectedEvent.title}</h5>

              <div className="mb-3">
                <strong>Start:</strong> {moment(selectedEvent.start).format('dddd, MMMM Do YYYY, h:mm A')}
              </div>
              <div className="mb-3">
                <strong>End:</strong> {moment(selectedEvent.end).format('dddd, MMMM Do YYYY, h:mm A')}
              </div>

              {selectedEvent.resource?.description && (
                <div className="mb-3">
                  <strong>Description:</strong>
                  <p className="mt-1 text-muted">{selectedEvent.resource.description}</p>
                </div>
              )}

              {selectedEvent.resource?.location && (
                <div className="mb-3">
                  <strong>Location:</strong> {selectedEvent.resource.location}
                </div>
              )}

              {selectedEvent.resource?.hangoutLink && (
                <div className="mb-3">
                  <strong>Meeting Link:</strong>{' '}
                  <a href={selectedEvent.resource.hangoutLink} target="_blank" rel="noreferrer">
                    {selectedEvent.resource.hangoutLink}
                  </a>
                </div>
              )}

              {selectedEvent.resource?.attendees?.length > 0 && (
                <div className="mb-3">
                  <strong>Attendees:</strong>
                  <ul className="mt-1">
                    {selectedEvent.resource.attendees.map((attendee, index) => (
                      <li key={index}>{attendee.email}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedEvent?.resource?.hangoutLink && (
            <Button variant="primary" onClick={handleJoinMeet}>
              Join Google Meet
            </Button>
          )}
          <Button variant="secondary" onClick={closeEventModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}